import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CO2OptionsType } from '~/api/services/co2'
import Dialog from '~/components/Dialog'
import { FeedWrapper, StyledDivider, StyledFeed } from '../../style'
import { useFormContext, useWatch } from 'react-hook-form'
import InfoPopup from '~/components/InfoPopup'
import { findJuicyFeed, getTransformFeedGroups, totalJuicyFeedsSelected } from '~/pages/Farmer/Co2Climate/AnswerCO2Climate/utils'
import i18n from '~/components/i18n'
import { Message } from 'semantic-ui-react'
interface Props {
  feed: any
  isOpenFeedModal: boolean
  handleClose: (submited?: boolean) => void
  milkOptions: CO2OptionsType
  index: number
}

const AssignFeedModal: React.FC<Props> = (props) => {
  const { control, setValue, getValues } = useFormContext()
  const { feed, isOpenFeedModal, handleClose, milkOptions, index } = props
  const { t } = useTranslation()
  const watchPhaseEnabled = useWatch({ control, name: 'phaseFeeding' })

  const [feeds, setFeeds] = useState({
    coarseFeeds: [],
    concentratedFeeds: [],
    juicyFeeds: []
  })
  const [valueKey, setValueKey] = useState('feedGroups')

  useEffect(() => {
    if (isOpenFeedModal) {
      let valueKey = 'feedGroups'
      const feedingPhases = getValues(`feedGroups[${feed.feedGroupIndex}].feedingPhases`)
      if (watchPhaseEnabled && feedingPhases?.length > 0) {
        valueKey = (`feedGroups[${feed.feedGroupIndex}].feedingPhases[${feed.order - 1}]`)
        const feedValues = getValues(valueKey)
        setFeeds({
          coarseFeeds: feedValues['coarseFeeds'],
          concentratedFeeds: feedValues['concentratedFeeds'],
          juicyFeeds: feedValues['juicyFeeds']
        })
      } else {
        const feedValues = getValues(valueKey)
        const indexFeed = watchPhaseEnabled ? feed.feedGroupIndex : index
        setFeeds({
          coarseFeeds: feedValues[indexFeed]['coarseFeeds'],
          concentratedFeeds: feedValues[indexFeed]['concentratedFeeds'],
          juicyFeeds: feedValues[indexFeed]['juicyFeeds']
        })
      }
      setValueKey(valueKey)
    }
  }, [isOpenFeedModal, watchPhaseEnabled, index, feed])

  const feedOptions = [
    {
      title: t('Coarse Feeds'),
      key: 'coarseFeeds'
    },
    {
      title: t('Power and mineral feed'),
      key: 'concentratedFeeds'
    },
    {
      title: t('Juicy Feeds (Only one feed can be selected)'),
      key: 'juicyFeeds',
      info: t('Due to a specification of the LfL calculator, only one juice feed can be entered into the calculation. \n\
      Please select the feed you primarily use.')
    }
  ]

  function handleClickFeed(key, feedOption) {
    const index = feeds[key].findIndex(field => field['id'] === feedOption.id)
    if (index >= 0) {
      setFeeds({
        ...feeds,
        [key]: feeds[key].filter(feed => feed.id !== feedOption.id)
      })
      return
    }
    setFeeds({
      ...feeds,
      [key]: [...feeds[key], { ...feedOption, value: '' }]
    })
  }

  function renderConcentratedFeeds(feedOptions, title) {
    return <FeedWrapper style={{ marginLeft: 20 }}>
      <div style={{ width: 'fit-content' }}>
        <div className='feed-title'>{title}</div>
        <StyledDivider />
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {feedOptions.map(feedOption => {
          const active = feeds.concentratedFeeds.find(field => field['id'] === feedOption.id)
          return <div style={{ display: 'flex', alignItems: 'center', gap: 3 }} key={`concentratedFeeds-${feedOption.id}`}>
            <StyledFeed
              key={`concentratedFeeds-${feedOption.id}`}
              onClick={() => handleClickFeed('concentratedFeeds', feedOption)}
              className={active ? 'active-feed' : ''}
              basic>{feedOption.label}
            </StyledFeed>
            {feedOption.id === 15 && feedOption.group === 2 && <InfoPopup
              content={t('Select this field if you verifiably source soy produced in the EU or produce the soy yourself.')}
              styleIcon={{ fontSize: '1.3em' }}
              position={'top center'}
            />}
          </div>
        })}
      </div>
    </FeedWrapper>
  }

  function renderFeedElements(paneOption) {
    const { key, title } = paneOption
    const feedOptions = milkOptions[key]
    return <FeedWrapper>
      <div className='title'>
        <div style={{ display: 'flex', gap: 5 }}>
          <div>{title}</div>
          {paneOption?.info && <InfoPopup
            content={paneOption?.info}
            styleIcon={{ fontSize: '1.3em' }}
            position={'top center'}
          />}
        </div>
      </div>
      <StyledDivider />
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {feedOptions.map(feedOption => {
          const active = feeds[key].find(field => field['id'] === feedOption.id)
          // if on start i selected any juice feeds
          let disableFeed = key === 'juicyFeeds' && feeds.juicyFeeds.length > 0 && !active
          // check if i selected on previous feed groups juicyFeed
          if (key === 'juicyFeeds' && !active && !disableFeed) {
            const feedGroups = !watchPhaseEnabled ? getValues(valueKey) : getTransformFeedGroups(getValues('feedGroups'))
            const juicyFeedSelected = findJuicyFeed(feedGroups)
            const totalJuicyFeedsCount = totalJuicyFeedsSelected(feedGroups)
            // the last juicy feed deselected
            const lastJuicyFeedDeselected = feeds.juicyFeeds.length !== feedGroups[index].juicyFeeds.length && totalJuicyFeedsCount === 1
            disableFeed = juicyFeedSelected ? !lastJuicyFeedDeselected && (feedOption.id !== juicyFeedSelected?.id) : false
          }
          return <StyledFeed
            key={`${key}-${feedOption.id}`}
            onClick={() => !disableFeed && handleClickFeed(key, feedOption)}
            className={active ? 'active-feed' : disableFeed ? 'disabled' : ''}
            basic>{feedOption.label}</StyledFeed>
        })}
      </div>
    </FeedWrapper>
  }

  function renderFeeds() {
    return <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
      <Message info style={{ borderRadius: 10, marginLeft: 'auto', marginRight: 'auto', marginTop: 0 }}>
        <div>{t('The fields for your required feed appear colored after selection.')}</div>
      </Message>
      {feedOptions.map(paneOption => {
        if (paneOption.key !== 'concentratedFeeds') {
          return renderFeedElements(paneOption)
        }
        return <FeedWrapper key={paneOption.key}>
          <div className='title'>{paneOption.title}</div>
          <StyledDivider />
          {Object.keys(milkOptions[paneOption.key]).map((key) => {
            return renderConcentratedFeeds(milkOptions[paneOption.key][key], key)
          })}
        </FeedWrapper>
      })}
    </div>
  }

  function getNumberOfFeeds() {
    let sum = 0
    Object.keys(feeds).map(key => {
      sum += Object.values(feeds[key]).length
    })
    return sum
  }

  return <Dialog
    textHeader={t('Assignment of feed to the feed group {{name}}', { name: feed?.name || '' })}
    isDialogOpen={isOpenFeedModal}
    textSubmit={t('Assign feeds')}
    textClose={t('Cancel')}
    textContent={renderFeeds()}
    handleClose={() => handleClose()}
    disableSubmit={getNumberOfFeeds() === 0}
    handleSubmit={() => {
      let valueKey = 'feedGroups'
      const feedingPhase = getValues(`feedGroups[${feed.feedGroupIndex}].feedingPhases`)
      const feedingGroupHasPhase = feedingPhase?.length > 0 && watchPhaseEnabled
      if (feedingGroupHasPhase) {
        valueKey = (`feedGroups[${feed.feedGroupIndex}].feedingPhases[${feed.order - 1}]`)
      } else {
        valueKey = watchPhaseEnabled ? `${valueKey}[${feed.feedGroupIndex}]` : `${valueKey}[${index}]`
      }
      const feedGroup = getValues(valueKey)
      const idElement = feedingGroupHasPhase ? i18n.t('{{feedTitle}} / {{phaseTitle}}',
        {
          feedTitle: getValues(`feedGroups[${feed.feedGroupIndex}].name`), phaseTitle: feedGroup.name
        }) : feedGroup['name']
      const element = document.getElementById(idElement)
      if (element.classList.contains('error-feed-group')) {
        element.classList.remove('error-feed-group')
      }
      setValue(valueKey, {
        ...feedGroup,
        ...feeds
      })
      handleClose(true)
    }}
  />
}
export default AssignFeedModal